import {NgModule} from '@angular/core';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {CalculateDateDiff} from '@app/support/pipes/calculateDateDiff.pipe';
import {SmoothHeightComponent} from '@app/support/SmoothHeight.component';
import {MatButtonModule} from '@angular/material/button';
import {MatChipsModule} from '@angular/material/chips';
import {MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSelectModule} from '@angular/material/select';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TextMaskModule} from 'angular2-text-mask';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {IonicModule} from '@ionic/angular';
import {FilterPipe} from '@app/support/pipes/filter.pipe';
import {NgForTimes} from '@app/support/pipes/ngForTimes.pipe';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {AgmCoreModule} from '@agm/core';
import {environment} from '@env/environment';
import {Emojify} from '@app/support/pipes/emoji.pipe';
import {DateToString} from '@app/support/pipes/dateToString.pipe';
import {SnackbarComponent} from '@app/support/snackbars/snackbar.component';
import {ShiftComponent} from '@app/modules/shared/components/shift/shift';
import {TablePlaceholderComponent} from '@app/modules/shared/components/placeholders/tablePlaceholder.component';
import {MessageBoardPostPlaceholderComponent} from '@app/modules/shared/components/placeholders/messageBoardPostPlaceholder.component';
import {CardPlaceholderComponent} from '@app/modules/shared/components/placeholders/cardPlaceholder.component';
import {ChartPlaceholderComponent} from '@app/modules/shared/components/placeholders/chartPlaceholder.component';
import {ConversationPlaceholderComponent} from '@app/modules/shared/components/placeholders/conversationPlaceholder.component';
import {TranslateModule} from '@ngx-translate/core';
import {LocalDatePipe} from '@app/support/pipes/local-date.pipe';
import {LocalNumberPipe} from '@app/support/pipes/local-number.pipe';
import {ShiftService} from '@app/modules/shared/services/shift.service';
import {AvailabilityService} from '@app/modules/shared/services/availability.service';
import {DepartmentService} from '@app/modules/shared/services/department.service';
import {UserService} from '@app/modules/shared/services/user.service';
import {NotificationService} from '@app/modules/shared/services/notification.service';
import {GroupService} from '@app/modules/shared/services/group.service';
import {PunchUserService} from '@app/modules/shared/services/punchUser.service';
import {LocalCurrencyPipe} from '@app/support/pipes/local-currency.pipe';
import {FileEntryService} from '@app/modules/shared/services/fileEntry.service';
import {TenantService} from '@app/modules/shared/services/tenant.service';
import {MatMomentDateModule} from '@angular/material-moment-adapter';

import {AngularWebLibModule, ConfirmCanDeactivateGuard} from '@quickorder/angular-web-lib';
import {PayrollPlaceholderComponent} from './components/placeholders/payrollPlaceholder.component';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {NgxLinkifyjsModule} from 'ngx-linkifyjs';
import {RouterModule} from '@angular/router';
import {BulletinPostsComponent} from '@app/modules/shared/components/bulletin-posts/bulletin-posts.component';
import {FileEntryEditComponent} from '@app/modules/shared/components/file-entry-edit/file-entry-edit.component';
import {ClickOutsideDirective} from '@app/modules/shared/directives/click-outside.directive';
import {AutocompleteMultiselectComponent} from '@app/modules/shared/components/autocomplete-multiselect/autocomplete-multiselect.component';
import {BulletinBoardPostLikesInfoComponent} from '@app/modules/shared/components/bulletin-posts/components/bulletin-board-post-likes-info/bulletin-board-post-likes-info.component';
import {BulletinBoardPostSeenByInfoComponent} from '@app/modules/shared/components/bulletin-posts/components/bulletin-board-post-seen-by-info/bulletin-board-post-seen-by-info.component';
import {SingleBulletinBoardPostComponent} from '@app/modules/shared/components/bulletin-posts/components/single-bulletin-board-post/single-bulletin-board-post.component';
import {FileEntriesComponent} from './components/file-entries/file-entries.component';
import {HammerModule} from '@angular/platform-browser';
import {ReadMoreLessTextComponent} from '@app/modules/shared/components/read-more-less-text/read-more-less-text.component';
import {SickDayAnswersComponent} from '@app/modules/shared/components/sick-days/sick-day-answers/sick-day-answers.component';
import {SickDayAnswerEmployeeComponent} from '@app/modules/shared/components/sick-days/sick-day-answer-employee/sick-day-answer-employee.component';
import {CreateSickDayComponent} from '@app/modules/shared/components/sick-days/create-sick-day/create-sick-day.component';
import {SickDaysTableComponent} from '@app/modules/shared/components/sick-days/sick-days-table/sick-days-table.component';
import {PlanningShiftComponent} from '@app/modules/shared/components/planning-shift/planning-shift.component';
import {SelectWithImageComponent} from '@app/modules/shared/components/select-with-image/select-with-image.component';
import {ClickOutsideModule} from 'ng-click-outside';
import {MediaLibraryComponent} from '@app/modules/shared/components/media-library/media-library.component';
import {EmailExistsPopupComponent} from '@app/modules/shared/components/email-exists-popup/email-exists-popup.component';
import {QuickorderOoPopupComponent} from '@app/modules/shared/components/quickorder-oo-popup/quickorder-oo-popup.component';

export function getLocale(interService: InternationalizationService): any {
	return interService.currentLocales.angularLocale;
}

@NgModule({
	imports: [
		NgxChartsModule,
		MatMenuModule,
		MatButtonModule,
		MatSnackBarModule,
		MatInputModule,
		MatNativeDateModule,
		MatDatepickerModule,
		MatProgressBarModule,
		MatRippleModule,
		MatSelectModule,
		MatDialogModule,
		MatTooltipModule,
		TextMaskModule,
		MatExpansionModule,
		MatIconModule,
		FormsModule,
		IonicModule,
		DragDropModule,
		TranslateModule,
		MatMomentDateModule,
		AngularWebLibModule,
		RouterModule,
		HammerModule,

		// Google maps
		AgmCoreModule.forRoot({apiKey: (environment.production) ? 'AIzaSyCaMXCogF0AgmAIoHnrpskP8J8YITtbZJ8' : ''}),
		NgxLinkifyjsModule.forRoot(),
		MatChipsModule,
		ClickOutsideModule,
		ReactiveFormsModule,
	],
	declarations: [
		CalculateDateDiff,
		SmoothHeightComponent,
		FilterPipe,
		NgForTimes,
		Emojify,
		DateToString,
		ClickOutsideDirective,
		SnackbarComponent,
		ShiftComponent,
		ChartPlaceholderComponent,
		ConversationPlaceholderComponent,
		TablePlaceholderComponent,
		MessageBoardPostPlaceholderComponent,
		CardPlaceholderComponent,
		PayrollPlaceholderComponent,
		BulletinPostsComponent,
		AutocompleteMultiselectComponent,
		LocalDatePipe,
		LocalNumberPipe,
		LocalCurrencyPipe,
		FileEntryEditComponent,
		BulletinBoardPostLikesInfoComponent,
		BulletinBoardPostSeenByInfoComponent,
		SingleBulletinBoardPostComponent,
		FileEntriesComponent,
		ReadMoreLessTextComponent,
		SickDayAnswersComponent,
		SickDayAnswerEmployeeComponent,
		CreateSickDayComponent,
		SickDaysTableComponent,
		PlanningShiftComponent,
		SelectWithImageComponent,
		MediaLibraryComponent,
		EmailExistsPopupComponent,
		QuickorderOoPopupComponent,
	],
	entryComponents: [
		SnackbarComponent,
		BulletinBoardPostLikesInfoComponent,
		BulletinBoardPostSeenByInfoComponent,
		CreateSickDayComponent,
		SickDayAnswerEmployeeComponent,
		SickDayAnswersComponent,
		MediaLibraryComponent,
		EmailExistsPopupComponent,
		QuickorderOoPopupComponent,
	],
	providers: [
		LocalDatePipe,
		LocalNumberPipe,
		LocalCurrencyPipe,
		ShiftService,
		AvailabilityService,
		DepartmentService,
		UserService,
		NotificationService,
		GroupService,
		PunchUserService,
		FileEntryService,
		TenantService,
		ConfirmCanDeactivateGuard,
		{provide: MAT_DATE_LOCALE, useFactory: getLocale, deps: [InternationalizationService]},
	],
	exports: [
		ChartPlaceholderComponent,
		ConversationPlaceholderComponent,
		TablePlaceholderComponent,
		MessageBoardPostPlaceholderComponent,
		CardPlaceholderComponent,
		PayrollPlaceholderComponent,
		AutocompleteMultiselectComponent,
		SickDayAnswersComponent,
		SickDayAnswerEmployeeComponent,
		CreateSickDayComponent,
		SickDaysTableComponent,
		FormsModule,
		CalculateDateDiff,
		NgxChartsModule,
		MatMenuModule,
		MatButtonModule,
		MatSnackBarModule,
		MatInputModule,
		MatNativeDateModule,
		MatDatepickerModule,
		MatRippleModule,
		MatSelectModule,
		MatDialogModule,
		MatTooltipModule,
		MatProgressBarModule,
		SmoothHeightComponent,
		TextMaskModule,
		MatExpansionModule,
		MatIconModule,
		IonicModule,
		FilterPipe,
		NgForTimes,
		DragDropModule,
		AgmCoreModule,
		Emojify,
		DateToString,
		ClickOutsideDirective,
		SnackbarComponent,
		ShiftComponent,
		TranslateModule,
		FileEntryEditComponent,
		BulletinPostsComponent,
		LocalDatePipe,
		LocalNumberPipe,
		LocalCurrencyPipe,
		AngularWebLibModule,
		NgxLinkifyjsModule,
		RouterModule,
		SingleBulletinBoardPostComponent,
		BulletinBoardPostLikesInfoComponent,
		BulletinBoardPostSeenByInfoComponent,
		FileEntriesComponent,
		ReadMoreLessTextComponent,
		HammerModule,
		PlanningShiftComponent,
		SelectWithImageComponent,
		EmailExistsPopupComponent,
		QuickorderOoPopupComponent,
	],
})

export class SharedModule {
}
